import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendUpIcon,
  ClockIcon,
  FileTextIcon,
  SlidersIcon,
  ShieldIcon,
  AlertIcon
} from "../components/icons";
import ErrorState from "../components/ErrorState";
import HeroStatCard from "../components/charts/HeroStatCard";
import BarChartCard from "../components/charts/BarChartCard";
import DonutCard from "../components/charts/DonutCard";
import WidgetTile from "../components/WidgetTile";
import { SkeletonChart } from "../components/Skeleton";

import { transactionsToday, transactionsYesterday } from "../api/transaction.api";
import { dpdToday } from "../api/dpd.api";
import { uvToday } from "../api/uv.api";
import { usersToday } from "../api/user.api";

const TXN_LABELS = {
  loan_disbursement: "Disbursed",
  loan_payment: "Payment",
  loan_extension: "Extension",
  partial_payment: "Partial"
};

const UV_ORDER = ["Approved", "Rejected", "Rejected & Locked"];
const USER_STATUS_KEYS = ["Approved", "Pending", "Rejected", "Locked"];

function currency(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "₹0";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: null,
    txnToday: null,
    txnYesterday: null,
    dpd: null,
    uv: null,
    users: null
  });

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [today, yesterday, dpd, uv, users] = await Promise.allSettled([
        transactionsToday(),
        transactionsYesterday(),
        dpdToday(),
        uvToday(),
        usersToday()
      ]);

      setState({
        loading: false,
        error: null,
        txnToday: today.status === "fulfilled" ? today.value.data : null,
        txnYesterday: yesterday.status === "fulfilled" ? yesterday.value.data : null,
        dpd: dpd.status === "fulfilled" ? dpd.value.data : null,
        uv: uv.status === "fulfilled" ? uv.value.data?.summary || uv.value.data : null,
        users: users.status === "fulfilled" ? users.value.data : null
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message || "Failed to load live data" }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { loading, error, txnToday, txnYesterday, dpd, uv, users } = state;

  // STRICT VALIDATION FOR ALL METRICS — ZERO DUMMY DATA
  const disbursedToday = useMemo(() => {
    if (!Array.isArray(txnToday)) return null;
    return txnToday.find((t) => t.transaction_type === "loan_disbursement") || null;
  }, [txnToday]);

  const disbursedYesterday = useMemo(() => {
    if (!Array.isArray(txnYesterday)) return null;
    return txnYesterday.find((t) => t.transaction_type === "loan_disbursement") || null;
  }, [txnYesterday]);

  const paymentToday = useMemo(() => {
    if (!Array.isArray(txnToday)) return { count: 0, amount: 0 };
    const payments = txnToday.filter(
      (t) => t.transaction_type === "loan_payment" || t.transaction_type === "partial_payment"
    );
    return payments.reduce(
      (acc, curr) => ({
        count: acc.count + (curr.count || 0),
        amount: acc.amount + (curr.loan_amount_total || 0)
      }),
      { count: 0, amount: 0 }
    );
  }, [txnToday]);

  const extensionToday = useMemo(() => {
    if (!Array.isArray(txnToday)) return { count: 0, amount: 0 };
    const exts = txnToday.filter((t) => t.transaction_type === "loan_extension");
    return exts.reduce(
      (acc, curr) => ({
        count: acc.count + (curr.count || 0),
        amount: acc.amount + (curr.extension_charge_total || 0)
      }),
      { count: 0, amount: 0 }
    );
  }, [txnToday]);

  const disbursedDelta = useMemo(() => {
    if (
      disbursedToday &&
      typeof disbursedToday.loan_amount_total === "number" &&
      disbursedYesterday &&
      typeof disbursedYesterday.loan_amount_total === "number" &&
      disbursedYesterday.loan_amount_total > 0
    ) {
      return Math.round(
        ((disbursedToday.loan_amount_total - disbursedYesterday.loan_amount_total) /
          disbursedYesterday.loan_amount_total) *
          100
      );
    }
    return null;
  }, [disbursedToday, disbursedYesterday]);

  const txnBarData = useMemo(() => {
    if (!Array.isArray(txnToday) || txnToday.length === 0) return null;
    return txnToday.map((t) => ({
      label: TXN_LABELS[t.transaction_type] || t.transaction_type,
      value: typeof t.count === "number" ? t.count : 0
    }));
  }, [txnToday]);

  const userStatusData = useMemo(() => {
    if (!users || typeof users !== "object") return null;
    const items = USER_STATUS_KEYS.filter(
      (k) => typeof users[k] === "number" && !Number.isNaN(users[k]) && users[k] > 0
    ).map((k) => ({
      label: k,
      value: users[k]
    }));
    return items.length > 0 ? items : null;
  }, [users]);

  const uvDonutData = useMemo(() => {
    if (!uv || !Array.isArray(uv.message_summary) || uv.message_summary.length === 0) return null;
    return [...uv.message_summary]
      .filter((m) => m && typeof m.count === "number" && m.count > 0)
      .sort((a, b) => UV_ORDER.indexOf(a.message) - UV_ORDER.indexOf(b.message))
      .map((m) => ({ label: m.message, value: m.count }));
  }, [uv]);

  // Validated calculations for 6 Smart Action/Metric Widgets
  const totalLoans = dpd?.total_loans || 0;
  const zeroDpdCount = dpd?.DPD_counts?.["0"] || 0;
  const zeroDpdPct = totalLoans > 0 ? Math.round((zeroDpdCount / totalLoans) * 100) : 0;
  const overdueCount = Math.max(0, totalLoans - zeroDpdCount);

  const uvTotal = uv?.total_uv_processed || 0;
  const uvApproved = Array.isArray(uv?.message_summary)
    ? uv.message_summary.find((m) => m.message?.toLowerCase().includes("approved"))?.count || 0
    : 0;
  const uvApprovedPct = uvTotal > 0 ? Math.round((uvApproved / uvTotal) * 100) : 0;

  return (
    <div className="dashboard-container">
      {error && <ErrorState message={error} onRetry={load} />}

      {/* HERO & PRIMARY METRICS */}
      {!error && (
        <div className="overview-hero-section">
          {loading || !disbursedToday ? (
            <SkeletonChart />
          ) : (
            <HeroStatCard
              label="Disbursed Today"
              icon={TrendUpIcon}
              value={currency(disbursedToday.loan_amount_total)}
              sub={`${disbursedToday.count.toLocaleString("en-IN")} total disbursements`}
              delta={
                disbursedDelta === null
                  ? null
                  : `${disbursedDelta >= 0 ? "+" : ""}${disbursedDelta}% vs yesterday`
              }
            />
          )}
        </div>
      )}

      {/* 6 SMART ACTION & METRIC WIDGET TILES */}
      {!error && (
        <div className="smart-widgets-grid">
          {/* Widget 1: Purple (Disbursements) */}
          <WidgetTile
            title="Disbursements"
            value={disbursedToday ? currency(disbursedToday.loan_amount_total) : "₹0"}
            subtext={disbursedToday ? `${disbursedToday.count.toLocaleString("en-IN")} disbursements` : "No disbursements today"}
            icon={TrendUpIcon}
            colorScheme="purple"
            badge={disbursedDelta !== null ? `${disbursedDelta >= 0 ? "+" : ""}${disbursedDelta}%` : undefined}
            onClick={() => navigate("/transactions")}
          />

          {/* Widget 2: Rose (Collections) */}
          <WidgetTile
            title="Collections & Payments"
            value={currency(paymentToday.amount)}
            subtext={`${paymentToday.count.toLocaleString("en-IN")} payments collected`}
            icon={SlidersIcon}
            colorScheme="rose"
            onClick={() => navigate("/transactions")}
          />

          {/* Widget 3: Amber (Extensions) */}
          <WidgetTile
            title="Extension Charges"
            value={currency(extensionToday.amount)}
            subtext={`${extensionToday.count.toLocaleString("en-IN")} loan extensions`}
            icon={ClockIcon}
            colorScheme="amber"
            onClick={() => navigate("/transactions")}
          />

          {/* Widget 4: Emerald (Active Loans) */}
          <WidgetTile
            title="Active Loan Book"
            value={`${totalLoans.toLocaleString("en-IN")} loans`}
            subtext={`${zeroDpdPct}% on-time repayment (0 DPD)`}
            icon={FileTextIcon}
            colorScheme="emerald"
            onClick={() => navigate("/loans")}
          />

          {/* Widget 5: Coral (Overdue DPD) */}
          <WidgetTile
            title="Overdue Risk (DPD > 0)"
            value={`${overdueCount.toLocaleString("en-IN")} loans`}
            subtext={overdueCount > 0 ? "DPD risk spread monitored" : "Zero overdue loans"}
            icon={AlertIcon}
            colorScheme="coral"
            onClick={() => navigate("/dpd")}
          />

          {/* Widget 6: Violet (UV Rate) */}
          <WidgetTile
            title="UV Verification Rate"
            value={`${uvApprovedPct}% approved`}
            subtext={`${uvTotal.toLocaleString("en-IN")} verifications processed`}
            icon={ShieldIcon}
            colorScheme="violet"
            onClick={() => navigate("/uv")}
          />
        </div>
      )}

      {/* RECHARTS TRENDS & OUTCOMES */}
      {!error && (
        <div className="overview-charts-grid">
          {loading || !txnBarData ? (
            <SkeletonChart />
          ) : (
            <BarChartCard
              title="Transactions Today"
              subtitle="Volume by transaction type"
              data={txnBarData}
              formatter={(v) => `${v.toLocaleString("en-IN")} txns`}
            />
          )}

          {loading || !userStatusData ? (
            <SkeletonChart />
          ) : (
            <DonutCard
              title="Verification Status Breakdown"
              subtitle="Share of active users by verification state"
              data={userStatusData}
              centerLabel="users"
            />
          )}

          {loading || !uvDonutData ? (
            <SkeletonChart />
          ) : (
            <DonutCard
              title="Verification Outcomes"
              subtitle="UV today outcome share"
              data={uvDonutData}
              centerLabel="approved"
            />
          )}
        </div>
      )}
    </div>
  );
}
